import React, { useReducer, useCallback, useState, useRef, useMemo, useEffect } from "react";
import type { FC } from "react";

import { Center, Box, VStack, Button, Image, PresenceTransition } from "native-base";
import { Keyboard } from "react-native";
import { useMutation } from "react-relay";

import type {
  AuthStackNavigatorProps,
  AuthStackRouteProps,
} from "pianofunclub-crm/navigation/AuthNavigator";
import { LOGO_CROPPED_IMG } from "pianofunclub-crm/utils/assets";
import { saveAuthInfoToLocalStorage } from "pianofunclub-crm/utils/auth";

import TextInput from "pianofunclub-shared/components/Inputs/TextInput";
import type { TextInputProps, TextInputRef } from "pianofunclub-shared/components/Inputs/TextInput";
import AlertPopup from "pianofunclub-shared/components/NativeBaseExtended/AlertPopup";
import { MailIcon, SecurityIcon } from "pianofunclub-shared/components/Other/Icons";
import { useAuth } from "pianofunclub-shared/providers/AuthProviderContext";
import type {
  LoginMutation,
  LoginMutation$data,
} from "pianofunclub-shared/relay/graphql/auth/__generated__/LoginMutation.graphql";
import { login } from "pianofunclub-shared/relay/graphql/auth/Login";
import type { AlertError } from "pianofunclub-shared/types";
import { mapErrorToAlertText } from "pianofunclub-shared/utils/extractors";
import { createFormReducer } from "pianofunclub-shared/utils/reducers";

export type NavigationProps = AuthStackNavigatorProps<"Login">;

type RouteProps = AuthStackRouteProps<"Login">;

interface ScreenProps {
  navigation: NavigationProps;
  route: RouteProps;
}

type FormValues = {
  email: string;
  password: string;
};

const LoginScreen: FC<ScreenProps> = (props) => {
  const { navigation } = props;

  const { dispatchAuthState } = useAuth();

  const emailInputRef = useRef<TextInputRef>(null);
  const passwordInputRef = useRef<TextInputRef>(null);

  const [contentIsRendered, setContentIsRendered] = useState(false);
  const [failedLoginAlertIsOpen, setFailedLoginAlertIsOpen] = useState(false);
  const [failedError, setFailedError] = useState<AlertError>({
    alertHeader: "",
    alertMessage: "",
    code: undefined,
  });

  const initialState = useMemo(() => {
    return {
      formIsEdited: false,
      formIsValid: false,
      inputValidities: {
        email: false,
        password: false,
      },
      inputValues: {
        email: "",
        password: "",
      },
    };
  }, []);

  const formReducer = createFormReducer<FormValues>(initialState);
  const [formState, dispatchFormState] = useReducer(formReducer, initialState);

  const clearInputs = useCallback(() => {
    dispatchFormState({
      input: "email",
      isValid: false,
      value: "",
    });
    dispatchFormState({
      input: "password",
      isValid: false,
      value: "",
    });
    emailInputRef.current?.clear();
    passwordInputRef.current?.clear();
  }, []);

  // this is required to properly authenticate on the back-end
  const [commitLogin, loginInFlight] = useMutation<LoginMutation>(login);

  const loginUser = useCallback(
    (variables: { email: string; password: string }) => {
      Keyboard.dismiss();
      const loginConfig = {
        variables: {
          input: {
            email: variables.email,
            password: variables.password,
          },
        },
        onCompleted: (response: LoginMutation$data) => {
          if (response?.login?.success && response.login.token) {
            saveAuthInfoToLocalStorage({
              accountType: response.login.user?.profile?.accountType,
              refreshToken: response.login.refreshToken,
              refreshTokenExpiresAt: response.login.refreshTokenExpiresAt,
              token: response.login.token,
              tokenExpiresAt: response.login.tokenExpiresAt,
            });

            dispatchAuthState({
              accountType: response.login.user?.profile?.accountType ?? null,
              token: response.login.token,
              type: "ADD_TOKEN",
            });
          } else {
            const alertError = mapErrorToAlertText(response.login?.errors);
            setFailedError(alertError);

            if (alertError?.code === "invalid_credentials") {
              clearInputs();
            }
            setFailedLoginAlertIsOpen(true);
          }
        },
      };

      commitLogin(loginConfig);
    },
    [clearInputs, commitLogin, dispatchAuthState]
  );

  const inputChangeHandler = useCallback<NonNullable<TextInputProps["onInputChange"]>>(
    (inputIdentifier, inputValue, isValid) => {
      if (typeof inputValue === "string" && typeof inputIdentifier === "string") {
        dispatchFormState({
          input: inputIdentifier,
          isValid: isValid,
          value: inputValue.trim(),
        });
      }
    },
    [dispatchFormState]
  );

  useEffect(() => {
    setContentIsRendered(true);
  }, []);

  return (
    <>
      <PresenceTransition
        animate={{
          opacity: 1,
          transition: {
            delay: 250,
            duration: 1000,
          },
        }}
        initial={{
          opacity: 0,
        }}
        style={{ flex: 1 }}
        visible={contentIsRendered}
      >
        <Center flex={1} flexGrow={1} py="6" width="100%">
          <VStack alignItems="center" justifyContent="center" space="4" width="400px">
            <Image resizeMode="contain" size="xl" src={LOGO_CROPPED_IMG} />
            <Box key="emailInput" width="100%">
              <TextInput
                ref={emailInputRef}
                autoCapitalize="none"
                email
                id="email"
                initiallyValid={false}
                initialValue={""}
                InputLeftElement={
                  <MailIcon
                    color={
                      formState.inputValues.email === "" || !formState.inputValidities.email
                        ? "muted.400"
                        : "emerald.400"
                    }
                    ml="4"
                    size="6"
                  />
                }
                invalidIndicator
                keyboardType="email-address"
                label="Email"
                onInputChange={inputChangeHandler}
                onSubmit={() => passwordInputRef.current?.focus()}
                p="3"
                returnKeyType="next"
                submitBehavior="submit"
              />
            </Box>
            <Box key="passwordInput" width="100%">
              <TextInput
                ref={passwordInputRef}
                autoCapitalize="none"
                id="password"
                initiallyValid={false}
                initialValue=""
                InputLeftElement={
                  <SecurityIcon
                    color={
                      formState.inputValues.password == "" || !formState.inputValidities.password
                        ? "muted.400"
                        : "emerald.400"
                    }
                    ml="4"
                    size="6"
                  />
                }
                invalidIndicator
                keyboardType="default"
                label="Password"
                minLength={5}
                onInputChange={inputChangeHandler}
                onSubmit={() => {
                  if (formState.formIsValid) {
                    loginUser({
                      email: formState.inputValues.email.toLowerCase(),
                      password: formState.inputValues.password,
                    });
                  }
                }}
                p="3"
                secureTextEntry
              />
            </Box>
            <Button
              _pressed={{ bg: "transparent", opacity: 0.6 }}
              _text={{ color: "warmGray.400" }}
              colorScheme="warmGray"
              mt="2"
              onPress={() =>
                navigation.navigate("ForgotPassword", {
                  email: formState.inputValues.email,
                })
              }
              p="0.5"
              variant="ghost"
            >
              Forgotten your password?
            </Button>
            <Button
              _disabled={{ opacity: 0.6 }}
              _hover={{ bg: "primary.600" }}
              _pressed={{ bg: "primary.700" }}
              _spinner={{ size: "lg" }}
              _text={{ fontSize: "2xl" }}
              colorScheme="primary"
              isDisabled={!formState.formIsValid}
              isLoading={loginInFlight}
              mt="8"
              onPress={() =>
                loginUser({
                  email: formState.inputValues.email.toLowerCase(),
                  password: formState.inputValues.password,
                })
              }
              p="4"
              shadow={5}
              width="70%"
            >
              Login
            </Button>
          </VStack>
        </Center>
      </PresenceTransition>
      <AlertPopup
        alertIsOpen={failedLoginAlertIsOpen}
        body={"Check your email and password and try again."}
        closeOnOverlayClick
        header={failedError.alertHeader}
        setAlertIsOpen={setFailedLoginAlertIsOpen}
      >
        <Button
          _text={{ fontSize: "lg" }}
          colorScheme="primary"
          onPress={() => {
            setFailedLoginAlertIsOpen(false);
          }}
          width="40%"
        >
          Ok
        </Button>
      </AlertPopup>
    </>
  );
};

export const screenOptions = {
  headerShown: false,
};

export default LoginScreen;
